import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Field } from "@/components/admin/ui";
import { api, formatApiError } from "@/lib/api";
import { ORDER_STATUS, rupiah } from "@/lib/format";
import { packageTotal } from "@/lib/pricing";
import { useAdminTrips, useSettings } from "@/hooks/queries";

const EMPTY = {
  trip_id: "", package_id: "", start_date: "", participants: 1, customer_name: "", phone: "", email: "",
  notes: "", admin_notes: "", status: "menunggu", amount_paid: 0, total_price: "",
};

const fromOrder = (o) => ({ ...EMPTY, ...o, total_price: String(o.total_price) });

const Picker = ({ value, onChange, items, placeholder, testId }) => (
  <Select value={value || undefined} onValueChange={onChange}>
    <SelectTrigger data-testid={testId}><SelectValue placeholder={placeholder} /></SelectTrigger>
    <SelectContent>
      {items.map((i) => <SelectItem key={i.value} value={i.value} data-testid={`${testId}-option-${i.value}`}>{i.label}</SelectItem>)}
    </SelectContent>
  </Select>
);

export const OrderDialog = ({ order, onClose, onSaved }) => {
  const isEdit = !!order;
  const { data: trips = [] } = useAdminTrips();
  const { data: settings } = useSettings();
  const [form, setForm] = useState(() => (isEdit ? fromOrder(order) : EMPTY));
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const ev = (k) => (e) => set(k, e.target.value);
  const trip = trips.find((t) => t.id === form.trip_id);
  const pkg = trip?.packages.find((p) => p.id === form.package_id);
  const auto = pkg ? packageTotal(pkg, Number(form.participants)) : null;
  const total = form.total_price !== "" ? Number(form.total_price) : auto ?? 0;
  const dpPercent = settings?.dp_percent ?? 50;

  const save = useMutation({
    mutationFn: (payload) => (isEdit ? api.put(`/admin/orders/${order.id}`, payload) : api.post("/admin/orders", payload)),
    onSuccess: () => {
      toast.success(isEdit ? "Pesanan diperbarui" : "Pesanan ditambahkan");
      onSaved();
      onClose();
    },
    onError: (e) => toast.error(formatApiError(e)),
  });

  const submit = (e) => {
    e.preventDefault();
    const payload = {
      start_date: form.start_date, participants: Number(form.participants), customer_name: form.customer_name,
      phone: form.phone, email: form.email, notes: form.notes, admin_notes: form.admin_notes, status: form.status,
      amount_paid: Number(form.amount_paid) || 0,
    };
    if (form.total_price !== "") payload.total_price = Number(form.total_price);
    if (!isEdit) {
      if (!form.trip_id || !form.package_id) return toast.error("Pilih trip dan paket terlebih dahulu");
      Object.assign(payload, { trip_id: form.trip_id, package_id: form.package_id });
    }
    save.mutate(payload);
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="scroll-thin max-h-[92vh] max-w-2xl overflow-y-auto border-white/10 bg-ink-900" data-testid="order-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">{isEdit ? `Edit ${order.code}` : "Tambah pesanan"}</DialogTitle>
          <DialogDescription>{isEdit ? `${order.trip_name} · ${order.package_name}` : "Catat pesanan dari WhatsApp atau offline."}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="grid gap-4 sm:grid-cols-2">
          {!isEdit && (
            <>
              <Field label="Trip"><Picker value={form.trip_id} onChange={(v) => setForm((f) => ({ ...f, trip_id: v, package_id: "" }))} items={trips.map((t) => ({ value: t.id, label: t.name }))} placeholder="Pilih trip" testId="order-form-trip" /></Field>
              <Field label="Paket"><Picker value={form.package_id} onChange={(v) => set("package_id", v)} items={(trip?.packages || []).map((p) => ({ value: p.id, label: p.name }))} placeholder="Pilih paket" testId="order-form-package" /></Field>
            </>
          )}
          <Field label="Tanggal mulai"><Input type="date" required value={form.start_date} onChange={ev("start_date")} data-testid="order-form-date" /></Field>
          <Field label="Jumlah peserta"><Input type="number" min={1} required value={form.participants} onChange={ev("participants")} data-testid="order-form-participants" /></Field>
          <Field label="Nama tamu"><Input required minLength={2} value={form.customer_name} onChange={ev("customer_name")} data-testid="order-form-name" /></Field>
          <Field label="No. WhatsApp"><Input required minLength={8} value={form.phone} onChange={ev("phone")} data-testid="order-form-phone" /></Field>
          <Field label="Email" className="sm:col-span-2"><Input type="email" value={form.email} onChange={ev("email")} data-testid="order-form-email" /></Field>
          <Field label="Total harga (Rp)" hint={auto != null ? `Harga paket: ${rupiah(auto)}` : "Kosongkan untuk harga otomatis dari paket"}>
            <div className="flex gap-2">
              <Input type="number" min={0} value={form.total_price} onChange={ev("total_price")} placeholder={auto != null ? String(auto) : ""} data-testid="order-form-total" />
              {auto != null && <Button type="button" variant="outline" onClick={() => set("total_price", String(auto))} data-testid="order-form-auto-total">Otomatis</Button>}
            </div>
          </Field>
          <Field label="Sudah dibayar (Rp)" hint="Otomatis tercatat sebagai pemasukan.">
            <Input type="number" min={0} value={form.amount_paid} onChange={ev("amount_paid")} data-testid="order-form-paid" />
            <div className="flex gap-2 pt-1">
              <Button type="button" size="sm" variant="outline" onClick={() => setForm((f) => ({ ...f, amount_paid: Math.round((total * dpPercent) / 100), status: "dp" }))} data-testid="order-form-set-dp">DP {dpPercent}%</Button>
              <Button type="button" size="sm" variant="outline" onClick={() => setForm((f) => ({ ...f, amount_paid: total, status: "lunas" }))} data-testid="order-form-set-lunas">Lunas</Button>
            </div>
          </Field>
          <Field label="Status" className="sm:col-span-2">
            <Picker value={form.status} onChange={(v) => set("status", v)} items={Object.entries(ORDER_STATUS).map(([value, m]) => ({ value, label: m.label }))} testId="order-form-status" />
          </Field>
          <Field label="Catatan tamu"><Textarea rows={3} value={form.notes} onChange={ev("notes")} data-testid="order-form-notes" /></Field>
          <Field label="Catatan admin"><Textarea rows={3} value={form.admin_notes} onChange={ev("admin_notes")} data-testid="order-form-admin-notes" /></Field>
          <DialogFooter className="gap-2 sm:col-span-2">
            <Button type="button" variant="ghost" onClick={onClose} data-testid="order-form-cancel">Batal</Button>
            <Button type="submit" disabled={save.isPending} data-testid="order-form-submit">
              {save.isPending && <Loader2 className="animate-spin" />} Simpan
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
